import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, Star, MapPin, Calendar, Briefcase, ExternalLink, 
  Play, Mail, Calculator, Tag, Check, Video, Image, Link2, 
  Instagram, Youtube, Twitter, Globe, ChevronRight, Heart, Award, Zap, Shield, MessageSquare,
  Send, Loader2
} from 'lucide-react';
import Navbar from '../components/Navbar';
import { toast } from '../components/ui/sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://revocut-production.up.railway.app';

const FreelancerProfile = () => {
  const { freelancerId } = useParams();
  const navigate = useNavigate();
  const [freelancer, setFreelancer] = useState(null);
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('about'); // about, portfolio, reviews
  
  // Reviews state
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState({ total_count: 0, average_rating: 5, breakdown: {} });
  const [loadingReviews, setLoadingReviews] = useState(false);
  
  // Review form state
  const [canReview, setCanReview] = useState(false);
  const [reviewJobId, setReviewJobId] = useState(null);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({ rating: 5, title: '', comment: '' });
  const [submittingReview, setSubmittingReview] = useState(false);
  
  // Favorites
  const [isFavorited, setIsFavorited] = useState(false);
  const customerUser = JSON.parse(localStorage.getItem('customer_user') || 'null');

  useEffect(() => {
    const fetchFreelancerData = async () => {
      // CRITICAL: Fetch team data FIRST - this is the PRIMARY data source for public profiles
      try {
        const teamsRes = await fetch(`${API_URL}/api/teams`);
        if (teamsRes.ok) {
          const teams = await teamsRes.json();
          console.log('Teams loaded:', teams.length, 'Looking for freelancerId:', freelancerId);
          const matchingTeam = teams.find(t => {
            // Check all possible ID fields
            const match = t.freelancer_id === freelancerId || 
                         t.id === freelancerId || 
                         t.freelancer_user_id === freelancerId;
            if (match) {
              console.log('Found matching team:', t.name, t);
            }
            return match;
          });
          if (matchingTeam) {
            console.log('Setting team data:', matchingTeam.name);
            setTeam(matchingTeam);
          } else {
            console.log('No matching team found for freelancerId:', freelancerId);
          }
        }
      } catch (error) {
        console.error('Error fetching teams:', error);
      }

      // Try to fetch additional freelancer user data (optional - may not exist)
      try {
        const freelancerRes = await fetch(`${API_URL}/api/freelancer/${freelancerId}`);
        if (freelancerRes.ok) {
          const data = await freelancerRes.json();
          setFreelancer(data);
        }
      } catch (error) {
        // Silently ignore - freelancer user data is optional
        console.log('Freelancer user data not found (expected)');
      }
        
      // Check if favorited
      if (customerUser?.id) {
        try {
          const favRes = await fetch(`${API_URL}/api/customer/${customerUser.id}/favorites`);
          if (favRes.ok) {
            const favData = await favRes.json();
            setIsFavorited((favData.favorites || []).includes(freelancerId));
          }
        } catch (error) {
          console.log('Error fetching favorites:', error);
        }
      }
      
      setLoading(false);
    };

    fetchFreelancerData();
  }, [freelancerId, customerUser?.id]);
  
  // Fetch reviews when tab changes
  useEffect(() => {
    if (activeTab === 'reviews') {
      fetchReviews();
      checkCanReview();
    }
  }, [activeTab, freelancerId]);
  
  // Check if customer can write a review
  const checkCanReview = async () => {
    if (!customerUser?.id) {
      setCanReview(false);
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/reviews/can-review/${freelancerId}?customer_id=${customerUser.id}`);
      if (res.ok) {
        const data = await res.json();
        setCanReview(data.can_review);
        if (data.job_id) {
          setReviewJobId(data.job_id);
        }
      }
    } catch (error) {
      console.error('Error checking review permission:', error);
      setCanReview(false);
    }
  };
  
  const fetchReviews = async () => {
    setLoadingReviews(true);
    try {
      const res = await fetch(`${API_URL}/api/reviews/freelancer/${freelancerId}`);
      if (res.ok) {
        const data = await res.json();
        setReviews(data.reviews || []);
        setReviewStats({
          total_count: data.total_count || 0,
          average_rating: data.average_rating || 5,
          breakdown: data.breakdown || {}
        });
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoadingReviews(false);
    }
  };
  
  // Submit review
  const submitReview = async () => {
    if (!customerUser?.id || !reviewForm.comment.trim()) return;
    
    setSubmittingReview(true);
    try {
      const res = await fetch(`${API_URL}/api/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          freelancer_id: freelancerId,
          customer_id: customerUser.id,
          customer_name: customerUser.name,
          job_id: reviewJobId,
          rating: reviewForm.rating,
          title: reviewForm.title,
          comment: reviewForm.comment
        })
      });
      
      if (res.ok) {
        toast.success('Bewertung erfolgreich veröffentlicht!');
        setShowReviewForm(false);
        setReviewForm({ rating: 5, title: '', comment: '' });
        setCanReview(false); // Prevent double review
        fetchReviews(); // Refresh reviews
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Fehler beim Speichern');
      }
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error('Fehler beim Speichern der Bewertung');
    } finally {
      setSubmittingReview(false);
    }
  };

  const toggleFavorite = async () => {
    if (!customerUser?.id) {
      navigate('/login');
      return;
    }
    
    try {
      const method = isFavorited ? 'DELETE' : 'POST';
      const res = await fetch(`${API_URL}/api/customer/${customerUser.id}/favorites`, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ freelancer_id: freelancerId })
      });
      
      if (res.ok) {
        setIsFavorited(!isFavorited);
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };
  
  // Get seller level based on total_revenue
  const getSellerLevel = (totalRevenue) => {
    if (totalRevenue >= 100000) return { level: 10, label: 'Level 10', color: '#EC4899' };
    if (totalRevenue >= 75000) return { level: 9, label: 'Level 9', color: '#DC2626' };
    if (totalRevenue >= 50000) return { level: 8, label: 'Level 8', color: '#DC2626' };
    if (totalRevenue >= 35000) return { level: 7, label: 'Level 7', color: '#F97316' };
    if (totalRevenue >= 25000) return { level: 6, label: 'Level 6', color: '#F97316' };
    if (totalRevenue >= 15000) return { level: 5, label: 'Level 5', color: '#2563EB' };
    if (totalRevenue >= 10000) return { level: 4, label: 'Level 4', color: '#2563EB' };
    if (totalRevenue >= 5000) return { level: 3, label: 'Level 3', color: '#14B8A6' };
    if (totalRevenue >= 500) return { level: 2, label: 'Level 2', color: '#14B8A6' };
    return { level: 1, label: 'Level 1', color: '#16A34A' };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0a0a0a' }}>
        <div className="w-10 h-10 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Combine data from freelancer and team - TEAM DATA TAKES PRIORITY for public profile
  const profileData = {
    name: team?.name || freelancer?.name || 'Freelancer',
    email: team?.email || freelancer?.email || '',
    specialization: team?.specializations?.[0] || freelancer?.specialization || 'Video Editor',
    bio: team?.description || freelancer?.bio || '',
    location: team?.location?.city || freelancer?.location || 'Deutschland',
    rating: team?.rating || freelancer?.rating || 5.0,
    reviewCount: team?.reviews_count || freelancer?.review_count || 0,
    jobsCompleted: team?.completedProjects || freelancer?.completed_jobs_count || freelancer?.jobs_completed || 0,
    experienceYears: freelancer?.experience_years || 0,
    memberSince: freelancer?.created_at ? new Date(freelancer.created_at).getFullYear() : 2024,
    tags: team?.specializations || freelancer?.specialization_tags || freelancer?.specializations || ['Video Editor'],
    softwareSkills: freelancer?.software_skills || ['Adobe Premiere Pro', 'After Effects', 'DaVinci Resolve'],
    portfolioLinks: freelancer?.portfolio_links || [],
    socialLinks: freelancer?.social_links || {},
    profileImage: freelancer?.profile_image || null,
    basePrice: team?.prices?.basePerMinute || 80,
    isBoosted: team?.is_boosted || false
  };

  // Generate initials
  const initials = profileData.name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: '#0a0a0a' }}>
      {/* Navigation */}
      <Navbar />
      
      {/* Spacer for fixed navbar */}
      <div className="h-16" />

      <main className="px-6 lg:px-12 py-8 max-w-6xl mx-auto">
        {/* Profile Header - Social Media Style */}
        <div className="rounded-3xl border border-white/10 overflow-hidden mb-8" style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}>
          {/* Cover Image / Gradient */}
          <div 
            className="h-32 md:h-48"
            style={{ 
              background: 'linear-gradient(135deg, rgba(92, 99, 255, 0.3) 0%, rgba(92, 99, 255, 0.1) 100%)'
            }}
          />
          
          {/* Profile Info */}
          <div className="px-6 pb-6 -mt-16 md:-mt-20">
            <div className="flex flex-col md:flex-row md:items-end gap-6">
              {/* Avatar */}
              {profileData.profileImage ? (
                <img 
                  src={profileData.profileImage}
                  alt={profileData.name}
                  className="w-28 h-28 md:w-36 md:h-36 rounded-2xl border-4 border-[#0a0a0a] object-cover"
                />
              ) : (
                <div 
                  className="w-28 h-28 md:w-36 md:h-36 rounded-2xl border-4 border-[#0a0a0a] flex items-center justify-center text-4xl font-bold"
                  style={{ backgroundColor: '#5C63FF' }}
                >
                  {initials}
                </div>
              )}
              
              {/* Name & Info */}
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    {profileData.name}
                  </h1>
                  {/* Seller Level Badge */}
                  {(() => {
                    const level = getSellerLevel(team?.total_revenue || 0);
                    const isTopRated = profileData.rating >= 4.5;
                    return (
                      <div className="flex items-center gap-2">
                        <span 
                          className="text-xs font-bold px-3 py-1 rounded-full"
                          style={{ backgroundColor: `${level.color}25`, color: level.color, border: `1px solid ${level.color}40` }}
                        >
                          {level.label}
                        </span>
                        {isTopRated && (
                          <span className="text-xs font-bold px-3 py-1 rounded-full bg-orange-500/20 text-orange-400 border border-orange-500/40">
                            Top Rated
                          </span>
                        )}
                      </div>
                    );
                  })()}
                </div>
                <p className="text-lg text-gray-400 mb-3">{profileData.specialization}</p>
                
                <div className="flex flex-wrap items-center gap-4 text-sm">
                  <div className="flex items-center gap-1 text-yellow-500">
                    <Star className="w-4 h-4 fill-current" />
                    <span className="font-medium">{profileData.rating.toFixed(1)}</span>
                    {profileData.reviewCount > 0 && (
                      <span className="text-gray-400">({profileData.reviewCount} Bewertungen)</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 text-gray-400">
                    <MapPin className="w-4 h-4" />
                    <span>{profileData.location}</span>
                  </div>
                  <div className="flex items-center gap-1 text-gray-400">
                    <Calendar className="w-4 h-4" />
                    <span>Seit {profileData.memberSince}</span>
                  </div>
                  {profileData.jobsCompleted > 0 && (
                    <div className="flex items-center gap-1 text-gray-400">
                      <Briefcase className="w-4 h-4" />
                      <span>{profileData.jobsCompleted} Jobs</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={toggleFavorite}
                  className={`p-3 rounded-xl transition-all border ${
                    isFavorited 
                      ? 'border-red-500/50 bg-red-500/10' 
                      : 'border-white/10 hover:border-red-500/30 hover:bg-red-500/5'
                  }`}
                  title={isFavorited ? 'Aus Favoriten entfernen' : 'Zu Favoriten hinzufügen'}
                >
                  <Heart className={`w-5 h-5 ${isFavorited ? 'fill-red-500 text-red-500' : 'text-gray-400'}`} />
                </button>
                <button
                  onClick={() => navigate(`/calculator/${freelancerId}`)}
                  className="px-8 py-3 rounded-xl font-medium transition-all hover:opacity-90 flex items-center gap-2"
                  style={{ backgroundColor: '#5C63FF' }}
                  data-testid="book-btn"
                >
                  <Calculator className="w-5 h-5" />
                  Jetzt buchen
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {[
            { id: 'about', label: 'Über mich' },
            { id: 'portfolio', label: 'Portfolio' },
            { id: 'reviews', label: 'Bewertungen' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 rounded-xl font-medium transition-all whitespace-nowrap ${
                activeTab === tab.id 
                  ? 'bg-purple-500 text-white' 
                  : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* About Tab */}
            {activeTab === 'about' && (
              <>
                {/* Bio */}
                {profileData.bio && (
                  <div className="rounded-2xl border border-white/10 p-6" style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}>
                    <h3 className="font-bold mb-4">Über mich</h3>
                    <p className="text-gray-300 whitespace-pre-line">{profileData.bio}</p>
                  </div>
                )}

                {/* Tags / Spezialisierungen */}
                {profileData.tags.length > 0 && (
                  <div className="rounded-2xl border border-white/10 p-6" style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}>
                    <h3 className="font-bold mb-4 flex items-center gap-2">
                      <Tag className="w-5 h-5 text-purple-400" />
                      Spezialisierungen
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {profileData.tags.map(tag => (
                        <span
                          key={tag}
                          className="px-4 py-2 rounded-xl text-sm"
                          style={{ backgroundColor: 'rgba(92, 99, 255, 0.15)', color: '#5C63FF' }}
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Software Skills */}
                {profileData.softwareSkills.length > 0 && (
                  <div className="rounded-2xl border border-white/10 p-6" style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}>
                    <h3 className="font-bold mb-4">Software & Tools</h3>
                    <div className="flex flex-wrap gap-2">
                      {profileData.softwareSkills.map(skill => (
                        <span
                          key={skill}
                          className="px-4 py-2 rounded-xl text-sm flex items-center gap-2"
                          style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}
                        >
                          <Check className="w-4 h-4 text-green-400" />
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Portfolio Tab */}
            {activeTab === 'portfolio' && (
              <div className="space-y-6">
                {profileData.portfolioLinks.length > 0 ? (
                  <>
                    {/* Video Links */}
                    <div className="rounded-2xl border border-white/10 p-6" style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}>
                      <h3 className="font-bold mb-4 flex items-center gap-2">
                        <Video className="w-5 h-5 text-purple-400" />
                        Portfolio Links
                      </h3>
                      <div className="space-y-3">
                        {profileData.portfolioLinks.map((link, idx) => (
                          <a
                            key={idx}
                            href={link.url || link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-3 p-4 rounded-xl hover:bg-white/5 transition-colors group"
                          >
                            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(92, 99, 255, 0.15)' }}>
                              <Play className="w-5 h-5" style={{ color: '#5C63FF' }} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate group-hover:text-purple-400 transition-colors">
                                {link.title || link.url || link}
                              </p>
                              <p className="text-sm text-gray-500 truncate">{link.url || link}</p>
                            </div>
                            <ExternalLink className="w-5 h-5 text-gray-500 group-hover:text-purple-400" />
                          </a>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="rounded-2xl border border-white/10 p-12 text-center" style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}>
                    <Image className="w-16 h-16 mx-auto mb-4 text-gray-600" />
                    <h3 className="text-xl font-bold mb-2">Noch kein Portfolio</h3>
                    <p className="text-gray-400">
                      Dieser Freelancer hat noch keine Portfolio-Arbeiten hochgeladen.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Reviews Tab */}
            {activeTab === 'reviews' && (
              <div className="space-y-6">
                {/* Write Review Button/Form */}
                {canReview && !showReviewForm && (
                  <div 
                    className="rounded-2xl border border-purple-500/30 p-6"
                    style={{ backgroundColor: 'rgba(92, 99, 255, 0.05)' }}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-bold mb-1">Teile deine Erfahrung!</h3>
                        <p className="text-sm text-gray-400">
                          Du hast mit diesem Freelancer gearbeitet. Schreibe eine Bewertung.
                        </p>
                      </div>
                      <button
                        onClick={() => setShowReviewForm(true)}
                        className="px-6 py-3 rounded-xl font-medium transition-all hover:opacity-90 flex items-center gap-2"
                        style={{ backgroundColor: '#5C63FF' }}
                        data-testid="write-review-btn"
                      >
                        <Star className="w-5 h-5" />
                        Bewertung schreiben
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Review Form */}
                {showReviewForm && (
                  <div 
                    className="rounded-2xl border border-white/10 p-6"
                    style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}
                  >
                    <h3 className="font-bold mb-4">Deine Bewertung</h3>
                    
                    {/* Star Rating */}
                    <div className="mb-4">
                      <label className="text-sm text-gray-400 mb-2 block">Bewertung</label>
                      <div className="flex gap-2">
                        {[1, 2, 3, 4, 5].map(star => (
                          <button
                            key={star}
                            onClick={() => setReviewForm({ ...reviewForm, rating: star })}
                            className="p-1 transition-transform hover:scale-110"
                          >
                            <Star 
                              className={`w-8 h-8 ${star <= reviewForm.rating ? 'fill-yellow-500 text-yellow-500' : 'text-gray-600'}`}
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {/* Title */}
                    <div className="mb-4">
                      <label className="text-sm text-gray-400 mb-2 block">Titel (optional)</label>
                      <input
                        type="text"
                        value={reviewForm.title}
                        onChange={(e) => setReviewForm({ ...reviewForm, title: e.target.value })}
                        placeholder="z.B. Tolle Zusammenarbeit!"
                        className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-purple-500 outline-none transition-colors"
                      />
                    </div>
                    
                    {/* Comment */}
                    <div className="mb-4">
                      <label className="text-sm text-gray-400 mb-2 block">Dein Feedback *</label>
                      <textarea
                        value={reviewForm.comment}
                        onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                        placeholder="Beschreibe deine Erfahrung mit diesem Freelancer..."
                        rows={4}
                        className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-purple-500 outline-none transition-colors resize-none"
                      />
                    </div>
                    
                    {/* Actions */}
                    <div className="flex gap-3">
                      <button
                        onClick={submitReview}
                        disabled={submittingReview || !reviewForm.comment.trim()}
                        className="px-6 py-3 rounded-xl font-medium transition-all hover:opacity-90 flex items-center gap-2 disabled:opacity-50"
                        style={{ backgroundColor: '#5C63FF' }}
                        data-testid="submit-review-btn"
                      >
                        {submittingReview ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <Send className="w-5 h-5" />
                        )}
                        Bewertung absenden
                      </button>
                      <button
                        onClick={() => {
                          setShowReviewForm(false);
                          setReviewForm({ rating: 5, title: '', comment: '' });
                        }}
                        className="px-6 py-3 rounded-xl font-medium transition-all hover:bg-white/10 border border-white/10"
                      >
                        Abbrechen
                      </button>
                    </div>
                  </div>
                )}
                
                {loadingReviews ? (
                  <div className="flex items-center justify-center py-20">
                    <div className="w-10 h-10 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : reviews.length === 0 ? (
                  <div className="rounded-2xl border border-white/10 p-12 text-center" style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}>
                    <Star className="w-16 h-16 mx-auto mb-4 text-gray-600" />
                    <h3 className="text-xl font-bold mb-2">Noch keine Bewertungen</h3>
                    <p className="text-gray-400">
                      Dieser Freelancer hat noch keine Bewertungen erhalten.
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Rating Overview */}
                    <div className="rounded-2xl border border-white/10 p-6" style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}>
                      <div className="flex items-center gap-8">
                        <div className="text-center">
                          <div className="text-5xl font-bold text-yellow-500 mb-1">
                            {reviewStats.average_rating.toFixed(1)}
                          </div>
                          <div className="flex items-center justify-center gap-1 mb-1">
                            {[1, 2, 3, 4, 5].map(i => (
                              <Star
                                key={i}
                                className={`w-4 h-4 ${i <= Math.round(reviewStats.average_rating) ? 'fill-yellow-500 text-yellow-500' : 'text-gray-600'}`}
                              />
                            ))}
                          </div>
                          <p className="text-sm text-gray-400">{reviewStats.total_count} Bewertungen</p>
                        </div>
                        
                        {/* Rating Breakdown */}
                        <div className="flex-1 space-y-2">
                          {[5, 4, 3, 2, 1].map(rating => {
                            const count = reviewStats.breakdown[rating] || 0;
                            const percentage = reviewStats.total_count > 0 
                              ? (count / reviewStats.total_count) * 100 
                              : 0;
                            return (
                              <div key={rating} className="flex items-center gap-2">
                                <span className="text-sm text-gray-400 w-4">{rating}</span>
                                <Star className="w-3 h-3 fill-yellow-500 text-yellow-500" />
                                <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-yellow-500 rounded-full"
                                    style={{ width: `${percentage}%` }}
                                  />
                                </div>
                                <span className="text-xs text-gray-500 w-8">{count}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    {/* Review List */}
                    <div className="space-y-4">
                      {reviews.map((review) => (
                        <div 
                          key={review.id}
                          className="rounded-2xl border border-white/10 p-6"
                          style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <div className="flex items-center gap-1 mb-1">
                                {[1, 2, 3, 4, 5].map(i => (
                                  <Star
                                    key={i}
                                    className={`w-4 h-4 ${i <= review.rating ? 'fill-yellow-500 text-yellow-500' : 'text-gray-600'}`}
                                  />
                                ))}
                              </div>
                              {review.customer_name && (
                                <p className="text-sm text-gray-400">{review.customer_name}</p>
                              )}
                            </div>
                            <span className="text-sm text-gray-500">
                              {new Date(review.created_at).toLocaleDateString('de-DE')}
                            </span>
                          </div>
                          {review.title && (
                            <h4 className="font-bold mb-2">{review.title}</h4>
                          )}
                          <p className="text-gray-300">{review.comment}</p>
                          {review.is_verified && (
                            <div className="mt-3 flex items-center gap-2 text-green-400 text-sm">
                              <Check className="w-4 h-4" />
                              Verifizierter Kauf
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Stats */}
            <div className="rounded-2xl border border-white/10 p-6" style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}>
              <h3 className="font-bold mb-4">Statistiken</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Bewertung</span>
                  <div className="flex items-center gap-1 text-yellow-500">
                    <Star className="w-4 h-4 fill-current" />
                    <span className="font-medium">{profileData.rating.toFixed(1)}</span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Abgeschlossene Jobs</span>
                  <span className="font-medium">{profileData.jobsCompleted}</span>
                </div>
                {profileData.experienceYears > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Erfahrung</span>
                    <span className="font-medium">{profileData.experienceYears} Jahre</span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Mitglied seit</span>
                  <span className="font-medium">{profileData.memberSince}</span>
                </div>
              </div>
            </div>

            {/* Social Links */}
            {Object.keys(profileData.socialLinks).length > 0 && (
              <div className="rounded-2xl border border-white/10 p-6" style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}>
                <h3 className="font-bold mb-4">Social Media</h3>
                <div className="space-y-3">
                  {profileData.socialLinks.instagram && (
                    <a
                      href={profileData.socialLinks.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors"
                    >
                      <Instagram className="w-5 h-5" />
                      <span>Instagram</span>
                    </a>
                  )}
                  {profileData.socialLinks.youtube && (
                    <a
                      href={profileData.socialLinks.youtube}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors"
                    >
                      <Youtube className="w-5 h-5" />
                      <span>YouTube</span>
                    </a>
                  )}
                  {profileData.socialLinks.twitter && (
                    <a
                      href={profileData.socialLinks.twitter}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors"
                    >
                      <Twitter className="w-5 h-5" />
                      <span>Twitter/X</span>
                    </a>
                  )}
                  {profileData.socialLinks.website && (
                    <a
                      href={profileData.socialLinks.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors"
                    >
                      <Globe className="w-5 h-5" />
                      <span>Website</span>
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Book Now CTA */}
            <div 
              className="rounded-2xl border border-purple-500/30 p-6"
              style={{ backgroundColor: 'rgba(92, 99, 255, 0.05)' }}
            >
              <h3 className="font-bold mb-2">Bereit zu starten?</h3>
              <p className="text-sm text-gray-400 mb-4">
                Kalkuliere jetzt dein Projekt mit {profileData.name.split(' ')[0]}.
              </p>
              <button
                onClick={() => navigate(`/calculator/${freelancerId}`)}
                className="w-full py-3 rounded-xl font-medium transition-all hover:opacity-90 flex items-center justify-center gap-2"
                style={{ backgroundColor: '#5C63FF' }}
              >
                <Calculator className="w-5 h-5" />
                Zum Preisrechner
              </button>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="px-6 lg:px-12 py-8 border-t border-white/10 mt-12">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 text-gray-500 text-sm">
          <p>© {new Date().getFullYear()} RevoCut. Alle Rechte vorbehalten.</p>
          <div className="flex gap-6">
            <Link to="/impressum" className="hover:text-white transition-colors">Impressum</Link>
            <Link to="/datenschutz" className="hover:text-white transition-colors">Datenschutz</Link>
            <Link to="/agb" className="hover:text-white transition-colors">AGB</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FreelancerProfile;
